import type { ProductItemCondition, VeilleHistoryState, VeilleState } from '@/shared/api/types/generated';
import type { GetUserVeillesQuery, GetVeilleFeedQuery } from '@/shared/hooks/queries/generated';
import type { ProductItemData } from '@/shared/types/product';

// ============================================================================
// Vue front d'une veille (mapping du fragment serveur VeilleBase) + mappers.
// ============================================================================

export type VeilleServer = GetUserVeillesQuery['getUserVeilles'][0];
export type VeilleFeedItemServer = GetVeilleFeedQuery['getVeilleFeed']['items'][0];

export interface VeilleView {
  id: string;
  name: string;
  /** Type front : le serveur dit `seller`, le reste de l'app dit `vendor`. */
  type: 'product' | 'vendor';
  /** paused occupe toujours son slot ; stopped l'a libéré (définitif). */
  state: VeilleState;
  createdAt: string;
  imageUrl: string | null;
  /** Lien Vinted du périmètre (partie inclusive seulement) — null avant 08-12. */
  pageUrl: string | null;
  /** Analyse d'origine — repasse à null si elle est supprimée (cas normal). */
  sourceAnalysisId: string | null;
  sellerVintedId: string | null;
  /** État du backfill des 7 jours d'historique, pas de la veille elle-même. */
  historyState: VeilleHistoryState;
  /** Périmètre à similarité (image/description) : alertes horaires sous-comptantes. */
  similarity: boolean;
  similarityDegraded: boolean;
  /** Filtre de création (ids) — sert à ré-afficher les critères. null en veille vendeur. */
  parsedFilter: Record<string, unknown> | null;
}

export const mapVeille = (veille: VeilleServer): VeilleView => {
  const parsedFilter = veille.parsedFilter as Record<string, unknown> | null | undefined;
  return {
    id: veille.id,
    name: veille.name,
    type: veille.type === 'seller' ? 'vendor' : 'product',
    state: veille.state,
    createdAt: veille.createdAt,
    imageUrl: veille.imageUrl ?? null,
    pageUrl: veille.pageUrl ?? null,
    sourceAnalysisId: veille.sourceAnalysisId ?? null,
    sellerVintedId: veille.sellerVintedId ?? null,
    historyState: veille.historyState,
    similarity: Boolean(parsedFilter?.imageKey || parsedFilter?.sourceProductId || parsedFilter?.description),
    similarityDegraded: veille.similarityDegraded,
    parsedFilter: parsedFilter ?? null,
  };
};

// ─── Feed : VeilleFeedItem serveur → carte produit du feed partagé ──────────

/** Libellés d'état serveur (dictionnaire des attributs) → enum front. */
const CONDITION_BY_TITLE: Record<string, ProductItemCondition> = {
  'Neuf avec étiquette': 'newWithTag',
  'Neuf sans étiquette': 'newWithoutTag',
  'Très bon état': 'veryGoodCondition',
  'Bon état': 'goodCondition',
  Satisfaisant: 'satisfactory',
  Bon: 'good',
};

/**
 * Mapping vers la carte du feed partagé. Les sept champs d'enrichissement
 * sont SOLIDAIRES (nuls d'un bloc si la ligne produit a été purgée, 30 j) :
 * la carte s'affiche quand même — jamais de ligne masquée.
 */
export const mapVeilleFeedItem = (item: VeilleFeedItemServer): ProductItemData => ({
  id: item.productId,
  url: item.url,
  title: item.title,
  description: null,
  price: item.price ?? 0,
  status: 'sold',
  itemCondition: item.conditionTitle ? (CONDITION_BY_TITLE[item.conditionTitle] ?? null) : null,
  favouriteCount: item.favouriteCount ?? 0,
  brand: item.brandTitle ?? null,
  sellerUrl: item.sellerId ? `https://www.vinted.fr/member/${item.sellerId}` : null,
  // sellerLogin ne survit pas à la purge du produit ; sellerId, oui
  sellerName: item.sellerLogin ?? null,
  sellerReviews: item.sellerFeedbackCount ?? null,
  sellerRating: item.sellerFeedbackRating ?? null,
  // soldAt = instant de DÉTECTION (latence ~1 h 30 - 3 h)
  uploadedAt: item.uploadedAt ?? item.soldAt,
  soldAt: item.soldAt,
  images: item.imageUrl ? [item.imageUrl] : [],
  attributes: [],
});
